/*
  Cart Source Tracker
  ---------------------------------------------------------
  Universal add-to-cart source detection.
  Wraps fetch() and XMLHttpRequest so every /cart/add.js call across
  the storefront gets `_Source` and `_Session` properties injected
  before it goes out — regardless of which app or theme code fired it.

  Also sets cart-level attributes on the first add of a session so
  every order carries first-touch marketing context.

  Data lands on:
    - Line item properties (per-item source, session)
    - Cart attributes (first source, first touch, landing page, session)

  Both surface on the Order detail page in Shopify Admin.
*/
(function () {
  'use strict';

  // ---------- Session / first-touch identity ----------

  function generateId(prefix) {
    return prefix + '-' +
      Date.now().toString(36).toUpperCase() + '-' +
      Math.random().toString(36).slice(2, 7).toUpperCase();
  }

  function sessionKey(k) {
    try { return sessionStorage.getItem(k); } catch (e) { return null; }
  }
  function sessionSet(k, v) {
    try { sessionStorage.setItem(k, v); } catch (e) {}
  }

  var SESSION_ID = sessionKey('rd_session') || (function () {
    var id = generateId('S');
    sessionSet('rd_session', id);
    return id;
  })();

  // Landing page = the first URL the customer hit this session
  if (!sessionKey('rd_landing')) {
    sessionSet('rd_landing', location.pathname + location.search);
  }
  var LANDING = sessionKey('rd_landing') || location.pathname;

  // First-touch source = where the customer came from (UTM > referrer > direct)
  var FIRST_TOUCH = sessionKey('rd_first_touch') || (function () {
    var v = 'Direct';
    try {
      var p = new URLSearchParams(location.search);
      var utm = p.get('utm_source');
      if (utm) v = 'UTM: ' + utm + (p.get('utm_medium') ? ' / ' + p.get('utm_medium') : '');
      else if (document.referrer) {
        var host = '';
        try { host = new URL(document.referrer).host; } catch (e) {}
        if (host && host.indexOf(location.host) === -1) v = 'Referrer: ' + host;
      }
    } catch (e) {}
    sessionSet('rd_first_touch', v);
    return v;
  })();

  // ---------- Source detection for a specific ATC ----------

  function productHandleFromPath(p) {
    var m = p.match(/^\/products\/([^\/\?]+)/);
    return m ? m[1] : 'unknown';
  }

  function detectSource(clickTarget) {
    var path = location.pathname;

    // Kit builder page — dedicated builder
    if (path.indexOf('/pages/build-your-kit') === 0) {
      return 'Kit Builder (builder page)';
    }

    // Kit configurator embedded on a PDP — check DOM
    if (clickTarget && clickTarget.closest && clickTarget.closest('[data-kit-configurator]')) {
      return 'Kit Builder (on ' + productHandleFromPath(path) + ' PDP)';
    }

    // Complementary upsell block (Shopify's "We recommend adding..")
    if (clickTarget && clickTarget.closest && clickTarget.closest('complementary-products, .complementary, [data-complementary]')) {
      return 'Complementary upsell — on ' + productHandleFromPath(path) + ' PDP';
    }

    // Cart-drawer recommendations
    if (clickTarget && clickTarget.closest && clickTarget.closest('cart-drawer, .cart-drawer, product-recommendations')) {
      return 'Cart drawer recommendation';
    }

    // Regular product PDP
    if (path.indexOf('/products/') === 0) {
      return 'Product page — ' + productHandleFromPath(path);
    }

    // Cart page (buy-again, saved-for-later, etc.)
    if (path === '/cart') return 'Cart page';

    // Collection page — quick-add card
    if (path.indexOf('/collections/') === 0) {
      var m = path.match(/^\/collections\/([^\/\?]+)/);
      return 'Collection quick-add — ' + (m ? m[1] : 'unknown');
    }

    // Home
    if (path === '/') return 'Homepage';

    return 'Other page — ' + path;
  }

  // Track the last user click for context in the fetch interceptor.
  var lastClickTarget = null;
  document.addEventListener('click', function (e) { lastClickTarget = e.target; }, true);

  // ---------- Inject properties into a request body ----------

  // Add tracking properties to a /cart/add body WITHOUT ever changing its
  // type. The previous version ran JSON.stringify() on every body — when the
  // theme's native Add-to-cart passed a FormData object, that produced the
  // string "{}", which replaced the real form fields and made Shopify reject
  // the add with HTTP 400 (silently breaking every native Add-to-cart button).
  // Each body type is now handled explicitly.
  function injectProps(body, source) {
    if (!body) return body;

    // FormData — the theme's native product form. Append fields; never stringify.
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      if (!body.has('properties[_Source]')) body.append('properties[_Source]', source);
      if (!body.has('properties[_Session]')) body.append('properties[_Session]', SESSION_ID);
      return body;
    }

    // URL-encoded form body.
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      if (!body.has('properties[_Source]')) body.append('properties[_Source]', source);
      if (!body.has('properties[_Session]')) body.append('properties[_Session]', SESSION_ID);
      return body;
    }

    // JSON string body — kit configurator and /cart/add.js callers.
    if (typeof body === 'string') {
      try {
        var parsed = JSON.parse(body);
        var apply = function (obj) {
          obj.properties = obj.properties || {};
          if (!obj.properties._Source) obj.properties._Source = source;
          if (!obj.properties._Session) obj.properties._Session = SESSION_ID;
        };
        if (parsed.items && Array.isArray(parsed.items)) parsed.items.forEach(apply);
        else if (parsed.id) apply(parsed);
        else return body; // unrecognized shape — leave untouched
        return JSON.stringify(parsed);
      } catch (e) {
        return body; // not JSON — leave untouched
      }
    }

    // Any other body type (Blob, ArrayBuffer, ReadableStream, …) — untouched.
    return body;
  }

  // ---------- Cart attributes — set once per session ----------

  var attrsSet = false;
  function setCartAttributes(source, origFetch) {
    if (attrsSet) return;
    attrsSet = true;
    setTimeout(function () {
      origFetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          attributes: {
            'First Source': source,
            'First Touch': FIRST_TOUCH,
            'Landing Page': LANDING,
            'Session': SESSION_ID
          }
        })
      }).catch(function () { /* non-blocking */ });
    }, 100);
  }

  // ---------- Wrap fetch ----------

  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url);
      if (url && /\/cart\/add(\.js)?/.test(url)) {
        init = init || {};
        var source = detectSource(lastClickTarget);
        if (init.body) init.body = injectProps(init.body, source);
        setCartAttributes(source, origFetch);
      }
    } catch (e) { /* non-blocking */ }
    return origFetch.apply(this, arguments);
  };

  // ---------- Wrap XHR (jQuery, older code) ----------

  var origOpen = XMLHttpRequest.prototype.open;
  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__cs_url = url;
    return origOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    try {
      if (this.__cs_url && /\/cart\/add(\.js)?/.test(this.__cs_url) && body) {
        var source = detectSource(lastClickTarget);
        body = injectProps(body, source);
        setCartAttributes(source, origFetch);
      }
    } catch (e) { /* non-blocking */ }
    return origSend.call(this, body);
  };
})();
